import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import FloatingActions from "@/components/FloatingActions";
import QuickActions from "@/components/QuickActions";
import StructuredData from "@/components/StructuredData";
import OrderDialog from "@/components/OrderDialog";
import { Palette, Home, Gift, Users, Phone, Mail, MapPin } from "lucide-react";
import { trackSocialClick, trackServiceAction } from "@/lib/analytics";
import { Link, useNavigate } from "react-router-dom";
import valeriaPhoto3 from "@/assets/valeria-wagner-3.png";

const Services = () => {
  const navigate = useNavigate();
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<{
    title: string;
    id: string;
    price: string;
  } | null>(null);

  const handleOrderClick = (service: { id: string; title: string; price: string }) => {
    trackServiceAction(service.id, service.title, 'order_click');
    setSelectedService(service);
    setOrderDialogOpen(true);
  };

  const services = [
    {
      id: "custom-paintings",
      title: "Индивидуальные картины на заказ",
      description: "Создание уникальных живописных произведений по вашим пожеланиям. Пейзажи, натюрморты любого размера и техники.",
      price: "от 6 000 ₽",
      features: ["Любой размер и формат", "Различные техники живописи", "Консультация художника", "Предварительные эскизы"],
      icon: Palette,
      popular: true
    },
    {
      id: "interior-art",
      title: "Декор интерьера картинами",
      description: "Подбор и создание картин специально для вашего интерьера в Омске. Учитываем стиль, цветовую гамму и размеры помещения.",
      price: "от 8 000 ₽",
      features: ["Выезд на объект в Омске", "Подбор по стилю интерьера", "Консультация дизайнера", "Гармония с интерьером"],
      icon: Home
    },
    {
      id: "gift-paintings",
      title: "Картины в подарок",
      description: "Создание памятных подарков - картины семьи, любимых мест, значимых моментов. Идеальный подарок на любой случай.",
      price: "от 4 000 ₽",
      features: ["Упаковка в подарочный вид", "Быстрые сроки выполнения", "Картины по фотографии", "Памятные композиции"],
      icon: Gift
    },
    {
      id: "corporate-art",
      title: "Корпоративные заказы",
      description: "Оформление офисов, ресторанов, гостиниц в Омске. Создание фирменного стиля через искусство.",
      price: "от 12 000 ₽",
      features: ["Разработка концепции", "Серии картин", "Корпоративные скидки", "Рассрочка платежа"],
      icon: Users
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Услуги художника в Омске - картины на заказ",
    "description": "Профессиональные услуги художника в Омске: картины на заказ, интерьерная живопись, подарочные картины. Быстро, качественно, доступно.",
    "provider": {
      "@type": "Organization",
      "name": "W Wagner Art",
      "url": "https://w-wagner.art",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Омск",
        "addressCountry": "RU"
      },
      "telephone": "+7 (908) 800-57-99",
      "email": "info@w-wagner.art"
    },
    "areaServed": {
      "@type": "City",
      "name": "Омск"
    },
    "serviceType": "Картины на заказ, Интерьерная живопись, Подарочные картины",
    "priceRange": "8000-50000 RUB"
  };


  return (
    <>
      <SEOHead 
        title="Услуги художника в Омске - картины на заказ | W Wagner Art"
        description="Профессиональные услуги художника в Омске: картины на заказ, интерьерная живопись, подарочные картины. Быстро, качественно, доступно. ☎ +7 (908) 800-57-99"
        keywords={["услуги художника омск", "картины на заказ омск", "интерьерная живопись", "подарочные картины омск"]}
        canonicalUrl="https://w-wagner.art/services"
      />
      <StructuredData type="about" data={structuredData} />
      
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-card via-background to-card/50">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,hsl(var(--primary)/0.1),transparent_50%)]" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
            <div className="text-center space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
                  <Palette className="w-4 h-4" />
                  Художник из Омска
                </div>
                <h1 className="text-6xl md:text-8xl font-bold">
                  <span className="bg-gradient-to-r from-primary via-accent to-primary bg-clip-text text-transparent">
                    Услуги
                  </span>
                </h1>
                <p className="text-2xl md:text-3xl text-muted-foreground font-light max-w-4xl mx-auto">
                  Профессиональное создание картин на заказ и интерьерной живописи. 
                  Воплощаем ваши идеи в искусстве с 2010 года.
                </p>
              </div>
              
              <div className="flex flex-wrap justify-center gap-3">
                {["Картины на заказ", "Интерьерная живопись", "Подарочные картины"].map((tag) => (
                  <Badge 
                    key={tag}
                    variant="outline" 
                    className="text-base px-6 py-3 border-primary/30 hover:border-primary/50 transition-colors"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-6 max-w-4xl mx-auto">
                Наши 
                <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent"> услуги</span>
              </h2>
              <p className="text-xl text-muted-foreground max-w-4xl mx-auto">
                Широкий спектр художественных услуг в Омске - от индивидуальных заказов до корпоративных проектов
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service) => {
                const IconComponent = service.icon;
                return (
                  <Card key={service.id} className="relative overflow-hidden border-2 hover:border-primary/30 transition-all duration-300 hover:shadow-lg">
                    {service.popular && (
                      <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground">
                        Популярно
                      </Badge>
                    )}
                    <CardHeader className="pb-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <IconComponent className="w-6 h-6 text-primary" />
                        </div>
                        <div className="text-2xl font-bold text-primary">{service.price}</div>
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                      <CardDescription className="text-base">
                        {service.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2 mb-6">
                        {service.features.map((feature, index) => (
                          <li key={index} className="flex items-center gap-2 text-sm">
                            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      <Button 
                        className="w-full"
                        onClick={() => handleOrderClick(service)}
                      >
                        Заказать услугу
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 hero-gradient relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent/6 via-primary/4 to-accent/8" />
          <div className="relative max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Как мы работаем с заказами</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Каждый заказ проходит продуманный путь от первой консультации до готового произведения
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="gallery-card text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-primary">1</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Обсуждение</h3>
                  <p className="text-muted-foreground text-sm">
                    Выясняем ваши пожелания, стиль и бюджет проекта
                  </p>
                </CardContent>
              </Card>
              
              <Card className="gallery-card text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-primary">2</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Эскиз</h3>
                  <p className="text-muted-foreground text-sm">
                    Создаем предварительный набросок для согласования
                  </p>
                </CardContent>
              </Card>
              
              <Card className="gallery-card text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-primary">3</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Работа</h3>
                  <p className="text-muted-foreground text-sm">
                    Профессиональное выполнение в мастерской в Омске
                  </p>
                </CardContent>
              </Card>
              
              <Card className="gallery-card text-center">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-primary">4</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Доставка</h3>
                  <p className="text-muted-foreground text-sm">
                    Готовую работу доставляем по Омску или забираете сами
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-16 md:py-20 px-4 sm:px-6 lg:px-8 bg-card/30">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div className="relative order-2 lg:order-1">
                <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-primary/10">
                  <img 
                    src={valeriaPhoto3} 
                    alt="Валерия Вагнер демонстрирует свою авторскую картину с изображением тигра. Художественная мастерская в Омске, профессиональные услуги живописи" 
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="absolute -inset-6 bg-gradient-to-r from-primary/20 to-accent/20 rounded-3xl -z-10 blur-2xl opacity-40" />
                
                {/* Floating achievement card */}
                <div className="absolute -top-6 -right-6 bg-card/95 backdrop-blur-sm border border-border/50 rounded-2xl p-4 shadow-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">5</div>
                    <div className="text-xs text-muted-foreground">лет опыта</div>
                  </div>
                </div>
                
                <div className="absolute -bottom-6 -left-6 bg-card/95 backdrop-blur-sm border border-border/50 rounded-2xl p-4 shadow-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-primary">20+</div>
                    <div className="text-xs text-muted-foreground">довольных клиентов</div>
                  </div>
                </div>

              </div>

              <div className="space-y-6 order-1 lg:order-2">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
                    <Palette className="w-4 h-4" />
                    Почему выбирают нас
                  </div>
                  <h2 className="text-3xl md:text-4xl font-bold">
                    Профессиональный подход к
                    <span className="text-primary"> каждому заказу</span>
                  </h2>
                  <p className="text-xl text-muted-foreground leading-relaxed">
                    В нашей мастерской каждая работа создается с особым вниманием к деталям и пожеланиям клиента.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-3"></div>
                    <div>
                      <h3 className="font-semibold mb-1">Индивидуальный подход</h3>
                      <p className="text-sm text-muted-foreground">Каждая работа создается с учетом ваших пожеланий и особенностей интерьера</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-3"></div>
                    <div>
                      <h3 className="font-semibold mb-1">Профессиональные материалы</h3>
                      <p className="text-sm text-muted-foreground">Используем только качественные краски и холсты для долговечности работ</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 rounded-full bg-primary mt-3"></div>
                    <div>
                      <h3 className="font-semibold mb-1">Работаем по всему Омску</h3>
                      <p className="text-sm text-muted-foreground">Доставка готовых работ по Омску и Омской области включена в стоимость</p>
                    </div>
                  </div>
                </div>
                
                {/* Блок QuickActions */}
                <div className="mt-6">
                  <QuickActions showTitle={true} />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Additional Services Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Дополнительные услуги</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Расширенный спектр художественных услуг для создания идеального результата
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Card className="gallery-card">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Palette className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Копии картин</h3>
                  <p className="text-muted-foreground text-sm">
                    Создание точных копий известных произведений и любимых картин
                  </p>
                </CardContent>
              </Card>
              
              <Card className="gallery-card">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Users className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Мастер-классы</h3>
                  <p className="text-muted-foreground text-sm">
                    Индивидуальные и групповые занятия по живописи для всех уровней
                  </p>
                </CardContent>
              </Card>
              
              <Card className="gallery-card">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Консультации</h3>
                  <p className="text-muted-foreground text-sm">
                    Художественные консультации по подбору картин для интерьера
                  </p>
                </CardContent>
              </Card>
              
              <Card className="gallery-card">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Home className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Оформление багетом</h3>
                  <p className="text-muted-foreground text-sm">
                    Профессиональное оформление готовых работ в багет по вашему выбору
                  </p>
                </CardContent>
              </Card>
              
              <Card className="gallery-card">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <MapPin className="w-8 h-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Выездная живопись</h3>
                  <p className="text-muted-foreground text-sm">
                    Создание картин на природе или в интересных локациях Омска
                  </p>
                </CardContent>
              </Card>
              
              <Card className="gallery-card">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-accent/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Phone className="w-8 h-8 text-accent" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Экспресс-картины</h3>
                  <p className="text-muted-foreground text-sm">
                    Быстрые картины за 1-2 дня для особых случаев и подарков
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-card via-background to-card border border-primary/10">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,hsl(var(--primary)/0.15),transparent_50%),radial-gradient(circle_at_70%_80%,hsl(var(--accent)/0.1),transparent_50%)]" />
              <div className="relative p-12 text-center">
                <div className="space-y-6">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 text-primary font-medium">
                    <Palette className="w-4 h-4" />
                    Начнем творить вместе
                  </div>
                  <h2 className="text-4xl md:text-5xl font-bold">
                    <span className="bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                      Обсудим ваш проект
                    </span>
                  </h2>
                  <p className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                    Расскажите о своих идеях, и мы создадим уникальное произведение искусства, 
                    которое превзойдет ваши ожидания
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
                    <Button 
                      size="lg" 
                      className="text-lg px-8 py-4 h-auto"
                      onClick={() => {
                        trackServiceAction('phone-call', 'Звонок с CTA', 'phone_click');
                        window.location.href = 'tel:+79088005799';
                      }}
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      +7 (908) 800-57-99
                    </Button>
                    <Button 
                      variant="outline" 
                      size="lg" 
                      className="text-lg px-8 py-4 h-auto"
                      onClick={() => {
                        trackServiceAction('contact-form', 'Написать сообщение', 'contact_click');
                        navigate('/contacts');
                      }}
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      Написать сообщение
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-center gap-4 pt-6">
                    <a 
                      href="https://wa.me/79507818785" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={() => trackSocialClick('WhatsApp', 'страница услуг')}
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-green-500/10 hover:bg-green-500/20 transition-colors group"
                    >
                      <svg className="w-6 h-6 text-green-600 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                      </svg>
                    </a>
                    <a 
                      href="https://t.me/FallenSonS"
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-500/10 hover:bg-blue-500/20 transition-colors group"
                    >
                      <svg className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                      </svg>
                    </a>
                    <a 
                      href="https://vk.com/im?sel=-214021218" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      onClick={() => trackSocialClick('VK Сообщения', 'services_page')}
                      className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-400/10 hover:bg-blue-400/20 transition-colors group"
                    >
                      <svg className="w-6 h-6 text-blue-500 group-hover:scale-110 transition-transform" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M15.684 0H8.316C1.592 0 0 1.592 0 8.316v7.368C0 22.408 1.592 24 8.316 24h7.368C22.408 24 24 22.408 24 15.684V8.316C24 1.592 22.408 0 15.684 0zM12 18c-3.314 0-6-2.686-6-6s2.686-6 6-6 6 2.686 6 6-2.686 6-6 6zm6-10.5c-.828 0-1.5-.672-1.5-1.5s.672-1.5 1.5-1.5 1.5.672 1.5 1.5-.672 1.5-1.5 1.5z"/>
                        <circle cx="12" cy="12" r="3" fill="white"/>
                        <text x="12" y="8" textAnchor="middle" fontSize="4" fill="currentColor">VK</text>
                      </svg>
                    </a>
                  </div>
                  
                  <div className="flex items-center justify-center gap-6 pt-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-green-500" />
                      Бесплатная консультация
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full bg-blue-500" />
                      Работаем по всему Омску
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <FloatingActions currentPage="services" />

        {/* Order Dialog */}
        {selectedService && (
          <OrderDialog
            artworkTitle={selectedService.title}
            artworkId={selectedService.id}
            artworkPrice={parseInt(selectedService.price.replace(/\D/g, '')) || 0}
            open={orderDialogOpen}
            onOpenChange={setOrderDialogOpen}
          />
        )}
      </div>
    </>
  );
};

export default Services;